/**
 * What CompanyCam is told about a job, and how a job is matched to a project
 * that already exists there.
 *
 * CompanyCam calls them **projects**; we call them leads when we talk. The API
 * word is used throughout this file so that nothing has to be translated at the
 * boundary.
 *
 * Every job creates or joins a project, the moment the job is created — a rep
 * standing in a driveway should find the photo album already waiting rather
 * than make one. The job that is *not* a real job is the exception: a test lead
 * is kept out of CompanyCam for the same reason it is kept out of the accounts
 * (migration 20260813130000).
 *
 * This module is the mapping and the matching, and nothing else: no network, no
 * token, no Supabase. The Edge Function transmits what these functions produce.
 * The one piece of logic that genuinely decides something — whether two
 * addresses are the same address — lives here so that it is unit-tested once,
 * rather than being written twice and drifting (CLAUDE.md rule 5).
 */

import { US_STATES } from "./constants.js";

/** CompanyCam's API. Bearer token in `Authorization`, JSON both ways. */
export const COMPANYCAM_API_BASE = "https://api.companycam.com/v2";

/**
 * How a job came to be linked to its project.
 *
 * `matched` means the project was already there and we joined it; `created`
 * means we made it. Worth keeping apart: a matched project belongs to whoever
 * made it and may carry another company's photos, so nothing here renames one.
 */
export const COMPANYCAM_LINK_SOURCES = ["matched", "created"] as const;

export type CompanycamLinkSource = (typeof COMPANYCAM_LINK_SOURCES)[number];

/** The states a queued link can be in. Mirrors the database CHECK. */
export const COMPANYCAM_LINK_STATES = [
  "pending",
  "linked",
  "failed",
  "abandoned",
] as const;

export type CompanycamLinkState = (typeof COMPANYCAM_LINK_STATES)[number];

// ------------------------------------------------------- the address key ---

/**
 * USPS suffixes and directionals, abbreviation to the one form we store.
 *
 * The canonical form is the long one, and which one it is does not matter in
 * the slightest — what matters is that both sides of a comparison go through
 * this same table. CompanyCam holds whatever a person typed into a phone;
 * Roof Board holds whatever Photon returned. "118 Cahaba Ridge Dr" and
 * "118 CAHABA RIDGE DRIVE" are one house, and a match that misses it creates a
 * second project on top of a job's existing photos.
 */
const DIRECTIONAL_TOKENS: Record<string, string> = {
  N: "NORTH",
  S: "SOUTH",
  E: "EAST",
  W: "WEST",
  NE: "NORTHEAST",
  NW: "NORTHWEST",
  SE: "SOUTHEAST",
  SW: "SOUTHWEST",
};

/**
 * Street suffixes, the ones that actually occur in this market.
 *
 * Kept apart from the directionals because `searchQuery` needs to tell them
 * apart: it peels at most one of each off the end of an address, and a rule
 * that peeled every suffix-shaped word would eat the street's own name —
 * "118 Cahaba Ridge Dr" would come back "118 Cahaba", because Ridge is on this
 * list too.
 */
const SUFFIX_TOKENS: Record<string, string> = {
  ST: "STREET",
  RD: "ROAD",
  DR: "DRIVE",
  AVE: "AVENUE",
  AV: "AVENUE",
  LN: "LANE",
  CT: "COURT",
  BLVD: "BOULEVARD",
  CIR: "CIRCLE",
  PL: "PLACE",
  TER: "TERRACE",
  TERR: "TERRACE",
  HWY: "HIGHWAY",
  PKWY: "PARKWAY",
  PKY: "PARKWAY",
  TRL: "TRAIL",
  PT: "POINT",
  XING: "CROSSING",
  CV: "COVE",
  HOLW: "HOLLOW",
  RDG: "RIDGE",
  VW: "VIEW",
  CRK: "CREEK",
  BND: "BEND",
  SQ: "SQUARE",
  PLZ: "PLAZA",
  EXPY: "EXPRESSWAY",
  FWY: "FREEWAY",
  RTE: "ROUTE",
  MT: "MOUNT",
};

/** Both tables together — what `normalizeStreet` rewrites, token by token. */
const CANONICAL_TOKENS: Record<string, string> = {
  ...DIRECTIONAL_TOKENS,
  ...SUFFIX_TOKENS,
};

/**
 * Unit designators, which are dropped from the match key.
 *
 * A duplex is one building and one roof. Apartment 2 of 118 Cahaba Ridge and
 * apartment 3 are photographed from the same driveway, and CompanyCam's own
 * address line usually has no unit on it at all. Everything from the
 * designator to the end of the line goes: `# 4`, `APT 4` and `APT4` are all the
 * same nothing.
 *
 * The stored address keeps the unit. Only the key loses it, because the key
 * exists to answer one question — is this the same building — and the unit is
 * not part of that answer.
 */
const UNIT_DESIGNATORS = new Set([
  "APT",
  "APARTMENT",
  "UNIT",
  "STE",
  "SUITE",
  "BLDG",
  "BUILDING",
  "FL",
  "FLOOR",
  "RM",
  "ROOM",
  "LOT",
  "TRLR",
  "TRAILER",
  "SPC",
  "SPACE",
  "DEPT",
  "NO",
]);

/** The bits of an address this needs. Anything carrying them will do. */
export interface AddressParts {
  address?: string | null | undefined;
  city?: string | null | undefined;
  state?: string | null | undefined;
  zip?: string | null | undefined;
}

/**
 * A street line, reduced to the form both sides are compared in.
 *
 * Uppercased, stripped of punctuation, collapsed, unit removed, abbreviations
 * expanded. `118 Cahaba Ridge Dr., Apt 2` and `118 CAHABA RIDGE DRIVE` both
 * come out `118 CAHABA RIDGE DRIVE`.
 */
export function normalizeStreet(street?: string | null): string {
  const cleaned = (street ?? "")
    .toUpperCase()
    // A hash is a unit designator wearing punctuation. Keep it as a word so the
    // unit stripper below can see it; everything else punctuation becomes space.
    .replace(/#/g, " NO ")
    .replace(/[^A-Z0-9 ]+/g, " ")
    .trim();
  if (!cleaned) return "";

  const out: string[] = [];
  for (const token of cleaned.split(/\s+/)) {
    // A designator ends the address as far as the key is concerned. Nothing
    // after it describes the building.
    if (UNIT_DESIGNATORS.has(token)) break;
    out.push(CANONICAL_TOKENS[token] ?? token);
  }
  return out.join(" ");
}

/** A city, in the same reduced form. */
export function normalizeCity(city?: string | null): string {
  return (city ?? "")
    .toUpperCase()
    .replace(/[^A-Z0-9 ]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * A state, as its two letters.
 *
 * Roof Board stores `AL`; CompanyCam takes whatever was typed, which is
 * sometimes `Alabama`.
 */
export function normalizeState(state?: string | null): string {
  const raw = (state ?? "").trim();
  if (!raw) return "";
  const spelled = Object.entries(US_STATES).find(
    ([name]) => name.toUpperCase() === raw.toUpperCase(),
  );
  if (spelled) return spelled[1];
  return raw
    .toUpperCase()
    .replace(/[^A-Z]/g, "")
    .slice(0, 2);
}

/**
 * Five digits of zip.
 *
 * Zip+4 is a delivery route, not a place; one side of a comparison having it is
 * not a difference of address.
 */
export function normalizeZip(zip?: string | null): string {
  const digits = (zip ?? "").replace(/[^0-9]/g, "");
  return digits.slice(0, 5);
}

/**
 * The whole address as one comparable string: `STREET|CITY|STATE|ZIP5`.
 *
 * Empty when there is no street, because a key without one would match every
 * other address in the same town and hand a job somebody else's photos. A job
 * with no street address simply does not get matched — it gets a project of its
 * own, which is the safe direction to fail in.
 */
export function addressKey(parts: AddressParts): string {
  const street = normalizeStreet(parts.address);
  if (!street) return "";
  return [
    street,
    normalizeCity(parts.city),
    normalizeState(parts.state),
    normalizeZip(parts.zip),
  ].join("|");
}

/**
 * What to ask CompanyCam for, when looking for a project at this address.
 *
 * This is not the match — `addressKey` is the match. This is the net, and a net
 * with the wrong mesh means the match never gets to run at all.
 *
 * CompanyCam filters on the text of address line 1. Roof Board sends what
 * Photon returned, which spells the suffix out: `1952 Cahaba Crest Drive`. A
 * project somebody typed on a phone says `1952 Cahaba Crest Dr`. Searching the
 * long form against the short one finds **nothing**, so the careful
 * normalisation below never sees the project that was sitting there, and a
 * second project gets created on top of the first. That happened on
 * 2026-08-23, job 1008 (ADR 0057).
 *
 * So the query stops before the suffix: `1952 Cahaba Crest` is a prefix of both
 * spellings. Trailing directionals go the same way — `500 1st Ave S` and
 * `500 1st Avenue South` both begin `500 1st`.
 *
 * Deliberately trimmed from the end and nowhere else. Dropping a word out of
 * the middle would break the very substring match this exists to satisfy.
 */
export function searchQuery(street?: string | null): string {
  const cleaned = (street ?? "")
    .replace(/[^A-Za-z0-9 ]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!cleaned) return "";

  const tokens = cleaned.split(" ");

  // Anything from a unit designator onwards describes a door, not a building.
  const unitAt = tokens.findIndex((token) =>
    UNIT_DESIGNATORS.has(token.toUpperCase()),
  );
  const kept = unitAt === -1 ? tokens.slice() : tokens.slice(0, unitAt);

  const isIn = (table: Record<string, string>, token: string) => {
    const upper = token.toUpperCase();
    return Boolean(table[upper]) || Object.values(table).includes(upper);
  };

  // At most one of each, from the end, in the order they occur:
  // "500 1st Ave S" loses S then Ave. Never so far that only the house number
  // is left — "1952" alone would match half of Hoover, and never so greedily
  // that a street called Ridge or Creek loses its own name.
  const peel = (table: Record<string, string>) => {
    if (kept.length > 2 && isIn(table, kept[kept.length - 1]!)) kept.pop();
  };
  peel(DIRECTIONAL_TOKENS);
  peel(SUFFIX_TOKENS);

  return (kept.length ? kept : tokens).join(" ");
}

// --------------------------------------------------------- coordinates ---

/** Close enough to be the same driveway. */
export const COORDINATE_MATCH_METRES = 30;

export interface Coordinates {
  lat: number;
  lng: number;
}

/** Metres between two points, on a sphere. Good to a few parts in a thousand. */
export function distanceMetres(a: Coordinates, b: Coordinates): number {
  const R = 6_371_000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** Coordinates, if the row has a usable pair. Zero/zero is not a place. */
export function coordinatesOf(input: {
  lat?: number | null | undefined;
  lng?: number | null | undefined;
}): Coordinates | null {
  const { lat, lng } = input;
  if (typeof lat !== "number" || typeof lng !== "number") return null;
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (lat === 0 && lng === 0) return null;
  return { lat, lng };
}

// ----------------------------------------------------- CompanyCam shapes ---

/** A project, as `GET /v2/projects` returns one. Only the parts we read. */
export interface CompanycamProject {
  id: number | string;
  name?: string | null;
  status?: string | null;
  archived?: boolean | null;
  address?: {
    street_address_1?: string | null;
    street_address_2?: string | null;
    city?: string | null;
    state?: string | null;
    postal_code?: string | null;
  } | null;
  coordinates?: { lat?: number | null; lon?: number | null } | null;
  project_url?: string | null;
  /** Unix seconds, as CompanyCam sends them. */
  created_at?: number | string | null;
}

/** A project's address, in the same key form as a job's. */
export function projectAddressKey(project: CompanycamProject): string {
  return addressKey({
    address: project.address?.street_address_1,
    city: project.address?.city,
    state: project.address?.state,
    zip: project.address?.postal_code,
  });
}

/** A project's coordinates. CompanyCam says `lon`; we say `lng`. */
export function projectCoordinates(
  project: CompanycamProject,
): Coordinates | null {
  return coordinatesOf({
    lat: project.coordinates?.lat,
    lng: project.coordinates?.lon,
  });
}

/**
 * A project that could hold this job's photos.
 *
 * Deleted and archived projects do not count. An archived project is one
 * somebody in CompanyCam decided was finished, and pouring a new job's photos
 * into it would un-finish it behind their back; a new project is the honest
 * answer. Everything else is fair game **however old it is** — the same house
 * hailed twice in five years is the same house, and its history is the point of
 * having photos at all.
 */
export function isLinkable(project: CompanycamProject): boolean {
  if (project.archived) return false;
  const status = (project.status ?? "active").toLowerCase();
  return status !== "deleted";
}

/** When a project was created, as a number that sorts. Unknown sorts oldest. */
export function projectCreatedAt(project: CompanycamProject): number {
  const raw = project.created_at;
  if (typeof raw === "number") return raw;
  if (typeof raw === "string" && raw.trim()) {
    const asNumber = Number(raw);
    if (Number.isFinite(asNumber)) return asNumber;
    const parsed = Date.parse(raw);
    if (Number.isFinite(parsed)) return parsed / 1000;
  }
  return 0;
}

/**
 * The project this job belongs to, out of everything the search returned.
 *
 * The normalized address is the answer. Coordinates are a second chance at it,
 * not a rival: CompanyCam's search filters on address line 1, so a project
 * typed as "118 Cahaba Rdg" comes back in the results but fails the key, and
 * thirty metres of agreement between two geocoders is enough to say it is the
 * same driveway anyway. Coordinates alone are never enough for a project the
 * search did not surface, because this only ever sees one page of one query.
 *
 * More than one survivor means the address has been photographed before, more
 * than once. The most recently created one wins: it is the one somebody is
 * working in now.
 */
export function pickProject(
  projects: readonly CompanycamProject[],
  job: { key: string; coordinates?: Coordinates | null },
): CompanycamProject | null {
  if (!job.key) return null;

  const candidates = projects.filter((project) => {
    if (!isLinkable(project)) return false;
    if (projectAddressKey(project) === job.key) return true;
    if (!job.coordinates) return false;
    const there = projectCoordinates(project);
    if (!there) return false;
    return distanceMetres(job.coordinates, there) <= COORDINATE_MATCH_METRES;
  });

  if (candidates.length === 0) return null;
  return candidates.reduce((newest, project) =>
    projectCreatedAt(project) > projectCreatedAt(newest) ? project : newest,
  );
}

// ------------------------------------------------ what a new project says ---

/** The parts of a contact CompanyCam is given. */
export interface ProjectContact {
  full_name?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  company?: string | null;
  email?: string | null;
  phone?: string | null;
  cell?: string | null;
}

/** The parts of a job CompanyCam is given. */
export interface ProjectJob {
  id?: string | null;
  job_number?: number | string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  lat?: number | null;
  lng?: number | null;
  job_type?: string | null;
  bid_type?: string | null;
}

/**
 * What the project is called in CompanyCam's list.
 *
 * **The customer's name, and nothing else.** Jonathon, 2026-08-27: "when a
 * CompanyCam is created ... it creates it with their first and last name as
 * the name of the project."
 *
 * It was "Whitlock — 231 Cahaba Rd" until then: surname and street, on the
 * reasoning that a rep searching for last Tuesday's roof types one or the
 * other. The street is redundant in the name — `companycamProject` sends the
 * address as its own field, so CompanyCam prints it under the project title
 * and searches it — and a title that reads like a person is what somebody
 * scrolling a list of them recognises.
 *
 * The fallbacks are still a chain, because an unnamed project is worse than a
 * badly named one: the full name as it is stored, then the company (a
 * commercial job has one and no surname), then the street, then the job
 * number.
 */
export function projectName(job: ProjectJob, contact?: ProjectContact): string {
  const parts = [contact?.first_name, contact?.last_name]
    .map((part) => (part ?? "").trim())
    .filter(Boolean)
    .join(" ");
  const person =
    parts ||
    (contact?.full_name ?? "").trim() ||
    (contact?.company ?? "").trim();
  if (person) return person;
  const street = (job.address ?? "").trim();
  if (street) return street;
  const number = `${job.job_number ?? ""}`.trim();
  return number ? `Job ${number}` : "Roof Board job";
}

/**
 * The roof types Roof Board records, as they are actually written.
 *
 * These are `roof_templates.roof_type`, copied onto an estimate when one is
 * built from a template. They are the vocabulary and not a guess: an estimate
 * built from scratch or as a custom repair has no roof type at all, and the
 * safe reading of nothing is not "shingle".
 *
 * Note what is absent. **TPO and PVC both record as `Flat / Low Slope`**, so a
 * label saying "TPO" cannot be produced from this data however much anyone
 * wants one — splitting those two would mean recording them separately first.
 */
export const COMPANYCAM_ROOF_TYPES = [
  "Shingle",
  "Metal Shingle",
  "Standing Seam Metal",
  "Exposed Fastener Metal Panel",
  "AG Panel 26 Gauge Residential",
  "R Panel 26 Gauge Residential",
  "Flat / Low Slope",
] as const;

/** How a job's type reads on a label. The column holds it lowercase. */
const JOB_TYPE_LABELS: Record<string, string> = {
  residential: "Residential",
  commercial: "Commercial",
};

/** How a bid type reads on a label. */
const BID_TYPE_LABELS: Record<string, string> = {
  repair: "Repair",
  retail: "Retail",
  insurance: "Insurance",
};

/** Every label Roof Board is allowed to take away again. See `reconcileLabels`. */
export function isOwnedLabel(
  label: string,
  /**
   * Names this job's own project was labelled with before 2026-08-23 — its rep
   * and whoever created it.
   *
   * A person's name is not a vocabulary and cannot be recognised by shape, so
   * the caller has to say which ones it wrote. Only the two names on *this*
   * job qualify: a name on somebody else's project is somebody else's label.
   */
  writtenNames: readonly string[] = [],
): boolean {
  const value = label.trim().toLowerCase();
  if (!value) return false;
  if (value === "roof board") return true;
  // The job number, and the uuid labels. Both were ways of pointing back at a
  // job; the notepad does that with a link. Owned here so the sync can take
  // the ones already out there back off (ADR 0076).
  if (/^job \d+$/.test(value)) return true;
  if (value.startsWith("rb:")) return true;
  if (JOB_TYPE_LABELS[value]) return true;
  if (BID_TYPE_LABELS[value]) return true;
  if (writtenNames.some((name) => name.trim().toLowerCase() === value)) {
    return true;
  }
  return COMPANYCAM_ROOF_TYPES.some((type) => type.toLowerCase() === value);
}

/** An estimate, as the labels need to see one. */
export interface LabelledEstimate {
  roof_type?: string | null;
  trade?: string | null;
}

/**
 * Whether a trade is replacement work. A repair says so in its name.
 *
 * Transcribed from `isReplacementTrade` in contract.ts rather than imported,
 * because this module is copied wholesale into the Edge Function and an import
 * would drag the contract machinery with it. `companycam.test.ts` pins the two
 * against each other.
 */
export function isReplacementWork(trade: string | null | undefined): boolean {
  const name = (trade ?? "").trim().toLowerCase();
  if (name === "") return false;
  return !name.includes("repair");
}

/**
 * The labels the project should carry, right now.
 *
 * Three things, and every one of them is something a person would filter a
 * CompanyCam list by:
 *
 * - **Residential / Commercial**, and **Repair / Retail / Insurance** — what
 *   kind of work this is.
 * - **The roof type**, on replacement work only. A repair is not a roofing
 *   system, and labelling one "Shingle" says something about the job that is
 *   not true.
 *
 * Jonathon cut the rest on 2026-08-23, looking at a project wearing six of
 * them (ADR 0076). A label is worth its space only if somebody would filter on
 * it, and `Roof Board` will be on every project once every project comes from
 * here; the rep's name is already recorded by CompanyCam itself; `Job 1015` and
 * the uuid were both ways of pointing back at a job, and the notepad does that
 * with a link that clicks.
 *
 * They stay in `isOwnedLabel`, deliberately. That is what lets the sync take
 * the ones already out there back off.
 *
 * The roof type arrives late by nature: it lives on the estimate, and a new
 * lead has no estimate. That is the reason these are re-synced rather than
 * written once (ADR 0057).
 */
export function projectLabels(input: {
  job: ProjectJob;
  /** The job's active estimates. Empty until somebody builds one. */
  estimates?: readonly LabelledEstimate[];
}): string[] {
  const labels: string[] = [];

  const type = JOB_TYPE_LABELS[(input.job.job_type ?? "").trim().toLowerCase()];
  if (type) labels.push(type);

  const bid = BID_TYPE_LABELS[(input.job.bid_type ?? "").trim().toLowerCase()];
  if (bid) labels.push(bid);

  // One job can sell two estimates at once (ADR 0030), and a roof and a
  // detached garage can be different systems. Every distinct one is a label.
  for (const estimate of input.estimates ?? []) {
    if (!isReplacementWork(estimate.trade)) continue;
    const roof = (estimate.roof_type ?? "").trim();
    if (!roof) continue;
    const known = COMPANYCAM_ROOF_TYPES.find(
      (candidate) => candidate.toLowerCase() === roof.toLowerCase(),
    );
    const label = known ?? roof;
    if (
      !labels.some((existing) => existing.toLowerCase() === label.toLowerCase())
    ) {
      labels.push(label);
    }
  }

  return labels;
}

/**
 * What to add and what to take away, so the project says what the job says.
 *
 * The asymmetry is the whole point. **Anything added is added; only what Roof
 * Board itself put there is ever removed.** A label a person typed on a
 * project — "Insurance Approved", "Needs Dumpster", the crew's own shorthand —
 * is none of this integration's business, and a sync that tidied those away
 * would be a sync nobody could trust with their own filing.
 *
 * A repair that becomes a replacement is the case this exists for: the project
 * said "Repair" from the day the lead was taken, and would go on saying it
 * forever, on a job that is now a full roof. A label that lies is worse than no
 * label at all.
 */
export function reconcileLabels(
  current: readonly string[],
  desired: readonly string[],
  /** This job's rep and creator — see `isOwnedLabel`. */
  writtenNames: readonly string[] = [],
): { add: string[]; remove: string[] } {
  const has = (list: readonly string[], label: string) =>
    list.some(
      (item) => item.trim().toLowerCase() === label.trim().toLowerCase(),
    );

  return {
    add: desired.filter((label) => !has(current, label)),
    remove: current.filter(
      (label) => isOwnedLabel(label, writtenNames) && !has(desired, label),
    ),
  };
}

/** The link back to the job, for the project's notepad. */
export function jobUrl(appUrl: string, jobId: string): string {
  return `${appUrl.replace(/\/+$/, "")}/#job=${jobId}`;
}

/** What the notepad says: where these photos came from, and how to get back. */
export function projectNotepad(
  job: ProjectJob,
  appUrl?: string | null,
): string {
  const lines = [`Roof Board job ${job.job_number ?? ""}`.trim()];
  const where = [
    (job.address ?? "").trim(),
    [(job.city ?? "").trim(), (job.state ?? "").trim()]
      .filter(Boolean)
      .join(", "),
    (job.zip ?? "").trim(),
  ]
    .filter(Boolean)
    .join(" ");
  if (where) lines.push(where);
  if (appUrl && job.id) lines.push(jobUrl(appUrl, job.id));
  return lines.join("\n");
}

/** A project, as `POST /v2/projects` takes it. */
export interface CompanycamProjectPayload {
  name: string;
  address?: {
    street_address_1: string;
    city?: string;
    state?: string;
    postal_code?: string;
    country?: string;
  };
  coordinates?: { lat: number; lon: number };
  primary_contact?: { name: string; email?: string; phone_number?: string };
}

/**
 * A job, as a new CompanyCam project.
 *
 * Only fields with something in them are sent: CompanyCam validates the contact
 * object once it exists, and a project should not fail to be created because
 * the customer has no email. `name` is the only thing the API insists on, and
 * `projectName` always produces one.
 */
export function companycamProject(input: {
  job: ProjectJob;
  contact?: ProjectContact | null;
}): CompanycamProjectPayload {
  const { job, contact } = input;
  const payload: CompanycamProjectPayload = {
    name: projectName(job, contact ?? undefined),
  };

  const street = (job.address ?? "").trim();
  if (street) {
    payload.address = { street_address_1: street, country: "US" };
    const city = (job.city ?? "").trim();
    const state = normalizeState(job.state);
    const zip = normalizeZip(job.zip);
    if (city) payload.address.city = city;
    if (state) payload.address.state = state;
    if (zip) payload.address.postal_code = zip;
  }

  const coordinates = coordinatesOf(job);
  if (coordinates) {
    payload.coordinates = { lat: coordinates.lat, lon: coordinates.lng };
  }

  const name =
    (contact?.full_name ?? "").trim() ||
    [contact?.first_name, contact?.last_name]
      .map((p) => (p ?? "").trim())
      .filter(Boolean)
      .join(" ") ||
    (contact?.company ?? "").trim();
  if (name) {
    payload.primary_contact = { name };
    const email = (contact?.email ?? "").trim();
    const phone = (contact?.cell ?? "").trim() || (contact?.phone ?? "").trim();
    if (email) payload.primary_contact.email = email;
    if (phone) payload.primary_contact.phone_number = phone;
  }

  return payload;
}

// ------------------------------------------------------------- the queue ---

/**
 * How long to wait before trying again, in minutes, per attempt already made.
 *
 * One minute, five, half an hour, two hours, half a day — then the row is
 * abandoned and a person has to press the button. The early attempts are for
 * the ordinary case, a blip; the later ones are for the case where CompanyCam
 * is having a bad afternoon. Nothing retries forever, because a row that has
 * failed five times is failing for a reason no amount of patience fixes.
 */
export const COMPANYCAM_BACKOFF_MINUTES = [1, 5, 30, 120, 720] as const;

/** After this many attempts the row is abandoned rather than retried. */
export const MAX_LINK_ATTEMPTS = COMPANYCAM_BACKOFF_MINUTES.length;

/** Minutes to wait after `attempts` failures, or null when there is no next try. */
export function retryDelayMinutes(attempts: number): number | null {
  if (attempts < 1) return COMPANYCAM_BACKOFF_MINUTES[0];
  if (attempts >= MAX_LINK_ATTEMPTS) return null;
  return COMPANYCAM_BACKOFF_MINUTES[attempts] ?? null;
}

/** The state a row moves to after a failure: retried, or given up on. */
export function stateAfterFailure(attempts: number): CompanycamLinkState {
  return attempts >= MAX_LINK_ATTEMPTS ? "abandoned" : "failed";
}

/**
 * What a link row records after a turn at the labels.
 *
 * **The flag only comes down when CompanyCam actually agreed.** A sync it
 * refused — a rate limit, a 500, a project somebody deleted in the app — has
 * changed nothing on the project, and lowering the flag anyway would strand
 * the very label this feature exists to correct: nothing raises it again until
 * somebody edits the job or an estimate afresh, so a project would go on
 * saying "Repair" on a full replacement with no retry left anywhere.
 *
 * `labels_synced_at` is left alone on a refusal for the same reason. It is the
 * last time the labels were known to be true, and advancing it on a failure
 * would make a stale project look freshly checked.
 */
export function linkRowAfterSync(
  settled: boolean,
  now: string,
): { needs_label_sync: boolean; labels_synced_at?: string } {
  return settled
    ? { needs_label_sync: false, labels_synced_at: now }
    : { needs_label_sync: true };
}

/**
 * What to say about a job's CompanyCam project on screen.
 *
 * The rep is standing at the house. "Not linked" with no explanation makes them
 * open CompanyCam and check by hand, which is the work this was supposed to
 * remove — so the failure says what failed and that it is coming back.
 */
export function companycamNotice(
  state: CompanycamLinkState,
  error?: string | null,
): string {
  const detail = (error ?? "").trim();
  switch (state) {
    case "pending":
      return "CompanyCam project on its way.";
    case "linked":
      return "Photos are in CompanyCam.";
    case "failed":
      return detail
        ? `CompanyCam not linked — retrying. ${detail}`
        : "CompanyCam not linked — retrying.";
    case "abandoned":
      return detail
        ? `CompanyCam gave up after ${MAX_LINK_ATTEMPTS} tries: ${detail}`
        : `CompanyCam gave up after ${MAX_LINK_ATTEMPTS} tries.`;
  }
}

// ------------------------------------------------------------- the lease ---

/**
 * How long a run may hold a queue row before another run may take it.
 *
 * A lease, not a state, because the thing being guarded against is two runs
 * overlapping — and a run that dies mid-flight must not lock a job out of its
 * project forever. Five minutes is far longer than a link takes (three seconds
 * for a lead taken this afternoon) and far shorter than a person's patience.
 *
 * This exists because it did not. On 2026-08-31 Tyler Christianson's job and
 * the Jacksons' both had two CompanyCam projects, each pair carrying the same
 * `rb:<job id>` label — Roof Board made both. Creating a lead fires the queue
 * push, the rep lands on the job page a second later, the row still says
 * pending, and the job page fires it again. Two runs, one row, no lock, two
 * projects (ADR 0085).
 */
export const COMPANYCAM_CLAIM_LEASE_MINUTES = 5;

/**
 * May a run take this row?
 *
 * True when nobody holds it, or when whoever did has held it past the lease.
 * The decision is here so it is tested; the *taking* is a conditional UPDATE in
 * the Edge Function, which is what makes it safe — asking this question and
 * then writing would be the same race one step further along.
 */
export function claimIsAvailable(
  claimedAt: string | null | undefined,
  now: Date = new Date(),
): boolean {
  if (!claimedAt) return true;
  const held = new Date(claimedAt).getTime();
  if (Number.isNaN(held)) return true;
  return now.getTime() - held >= COMPANYCAM_CLAIM_LEASE_MINUTES * 60_000;
}

/** The moment before which a claim counts as abandoned. */
export function claimCutoff(now: Date = new Date()): string {
  return new Date(
    now.getTime() - COMPANYCAM_CLAIM_LEASE_MINUTES * 60_000,
  ).toISOString();
}

/**
 * What to say when a job already has more CompanyCam projects than it should.
 *
 * Roof Board will not delete one — a project may have a rep's photos on it, and
 * which copy to keep is a person's call. This names the problem where somebody
 * will see it.
 */
export function duplicateProjectNotice(count: number): string | null {
  if (count < 2) return null;
  return (
    `${count} CompanyCam projects carry this job's tag. Roof Board uses the ` +
    `one linked below; merge or archive the others in CompanyCam.`
  );
}
